import React from 'react';
import { Button, Menu, MenuItem, ButtonProps } from '@mui/material';
import { Download } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

export type ExportFormat = 'excel' | 'csv';

export interface ExportButtonProps extends Omit<ButtonProps, 'onClick'> {
    data: any[];
    filename?: string;
    sheetName?: string;
    format?: ExportFormat | 'both';
    onExport?: (format: ExportFormat) => void;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
    data,
    filename = 'export',
    sheetName = 'Sheet1',
    format = 'both',
    onExport,
    children,
    ...buttonProps
}) => {
    const { t: translation } = useTranslation('reports');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (format === 'both') {
            setAnchorEl(event.currentTarget);
        } else {
            handleExport(format);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleExport = (exportFormat: ExportFormat) => {
        if (!data || data.length === 0) {
            console.warn(translation('NO_DATA_EXPORT'));
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        const timestamp = new Date().toISOString().split('T')[0];
        const fileExtension = exportFormat === 'excel' ? 'xlsx' : 'csv';
        const finalFilename = `${filename}_${timestamp}.${fileExtension}`;

        if (exportFormat === 'excel') {
            XLSX.writeFile(workbook, finalFilename);
        } else {
            XLSX.writeFile(workbook, finalFilename, { bookType: 'csv' });
        }

        onExport?.(exportFormat);
        handleClose();
    };

    return (
        <>
            <Button {...buttonProps} onClick={handleClick} startIcon={<Download />}>
                {children || translation('EXPORT')}
            </Button>
            {format === 'both' && (
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem onClick={() => handleExport('excel')}>{translation('EXPORT_EXCEL')}</MenuItem>
                    <MenuItem onClick={() => handleExport('csv')}>{translation('EXPORT_CSV')}</MenuItem>
                </Menu>
            )}
        </>
    );
};
